import * as React from "react";

interface ShadowRootContextValue {
  /** The funnel's shadow root, or null when rendered outside of one (e.g. in tests). */
  shadowRoot: ShadowRoot | null;
  /** Element inside the shadow root that portalled UI (dialogs, popovers, selects, date picker) renders into. */
  container: HTMLElement | null;
  /** Dedicated element for the flatpickr calendar. flatpickr treats everything inside its `appendTo` as part of the
   * calendar (so a click on it neither commits a typed date on blur nor closes the calendar) — it must not contain the app. */
  calendarContainer: HTMLElement | null;
}

const ShadowRootContext = React.createContext<ShadowRootContextValue>({ shadowRoot: null, container: null, calendarContainer: null });

export const ShadowRootProvider = ShadowRootContext.Provider;

/** Portal target for Radix `Portal`s. Portalling to `document.body` would escape the shadow root and lose all funnel styles. */
export const usePortalContainer = (): HTMLElement | undefined => React.useContext(ShadowRootContext).container ?? undefined;

/** Look up elements by id inside the funnel's shadow root (`document.getElementById` can't see into it). */
/** Where flatpickr appends its calendar (see `calendarContainer`). */
export const useCalendarContainer = (): HTMLElement | undefined => React.useContext(ShadowRootContext).calendarContainer ?? undefined;

export const useFunnelRoot = (): Document | ShadowRoot => React.useContext(ShadowRootContext).shadowRoot ?? document;
